import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { BillPaymentStatusEnum } from '@zaoblako/shared/modules/document/enums/bill.payment.status.enum';
import type { EmailFindInterface } from '@zaoblako/shared/modules/integration/dtos/email/email-find.dto';
import type { MainSmsAccountFindInterface } from '@zaoblako/shared/modules/integration/dtos/main-sms/main-sms-account-find.dto';
import { EmailGateEnum } from '@zaoblako/shared/modules/integration/enums/email.gate.enum';
import { MainSmsIntegrationTypeEnum } from '@zaoblako/shared/modules/integration/enums/main.sms.integration.type.enum';
import { SmsGateEnum } from '@zaoblako/shared/modules/integration/enums/sms.gate.enum';
import { MailingFileDebtorTargetFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-file-debtor-target-form.dto';
import {
  MailingTemplateFormDto,
  type MailingTemplateFormInterface
} from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-template-form.dto';
import type { MailingFileDebtorTargetFindInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-file-debtor-target-find.dto';
import { MailingAttachmentStatusEnum } from '@zaoblako/shared/modules/notification/enums/mailing-attachment-status.enum';
import { MailingChannelEnum } from '@zaoblako/shared/modules/notification/enums/mailing.channel.enum';
import { MailingTypeEnum } from '@zaoblako/shared/modules/notification/enums/mailing.type.enum';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { FinanceAccessService } from '@zaoblako/web/core/models/enums/access-rules/finance/finance-access.service';
import { BasisNotificationService } from '@zaoblako/web/core/services/basis/basis-notification.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { NewsletterTemplateHistoryWidgetComponent } from '@zaoblako/web/modules/debts/dumbs/newsletter-template-history-widget/newsletter-template-history-widget.component';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { DirectionMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-multi-select/direction-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { NewsletterEmailMainSmsSenderSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/newsletter-email-main-sms-sender-single-select/newsletter-email-main-sms-sender-single-select.component';
import { NewsletterEmailSenderSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/newsletter-email-sender-single-select/newsletter-email-sender-single-select.component';
import { NewsletterSmsSenderSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/newsletter-sms-sender-single-select/newsletter-sms-sender-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { PlanningPeriodicityCardComponent } from '@zaoblako/web/shared/dumbs/planning-periodicity-card/planning-periodicity-card.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { TemplateSubjectEditorComponent } from '@zaoblako/web/shared/dumbs/template-subject-editor/template-subject-editor.component';
import { TemplateSubjectInputComponent } from '@zaoblako/web/shared/dumbs/template-subject-input/template-subject-input.component';
import { CompanyMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-multi-select/company-multi-select.component';
import { BillPaymentStatusNamePipe } from '@zaoblako/web/shared/pipes/bill-payment-status-name.pipe';
import { JoinWithChildPipe } from '@zaoblako/web/shared/pipes/join-with-child.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { MailingAttachmentStatusNamePipe } from '@zaoblako/web/shared/pipes/mailing-attachment-status-name.pipe';
import { MailingChannelNamePipe } from '@zaoblako/web/shared/pipes/mailing-channel-name.pipe';
import { MailingTypeNamePipe } from '@zaoblako/web/shared/pipes/mailing-type-name.pipe';
import { PhonePipe } from '@zaoblako/web/shared/pipes/phone.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';

@Component({
  standalone: true,
  selector: 'crm-newsletter-template-form',
  templateUrl: './newsletter-template-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    TemplateSubjectInputComponent,
    TemplateSubjectEditorComponent,
    SingleSelectComponent,
    MailingTypeNamePipe,
    MailingChannelNamePipe,
    MultiSelectComponent,
    FileUploaderComponent,
    CheckboxModule,
    PlanningPeriodicityCardComponent,
    FormsModule,
    JoinWithChildPipe,
    TableComponent,
    PhonePipe,
    NewsletterTemplateHistoryWidgetComponent,
    ButtonComponent,
    GoBackButtonComponent,
    NewsletterEmailMainSmsSenderSingleSelectComponent,
    NewsletterEmailSenderSingleSelectComponent,
    NewsletterSmsSenderSingleSelectComponent,
    SelectButtonModule,
    UserMultiSelectComponent,
    UserGroupMultiSelectComponent,
    ContragentMultiSelectComponent,
    BillPaymentStatusNamePipe,
    DirectionMultiSelectComponent,
    InputNumberModule,
    DatepickerComponent,
    MailingAttachmentStatusNamePipe,
    CompanyMultiSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterTemplateFormComponent extends BaseFormComponent<MailingTemplateFormInterface> {
  @Input() public data = <MailingTemplateFormInterface>{};
  public dto = MailingTemplateFormDto;

  @Input() public emails: EmailFindInterface[] = [];
  @Input() public arraySms: MainSmsAccountFindInterface[] = [];
  @Input() public externalUsers: ExternalUserFindInterface[] = [];
  @Input() public recipientUsers: UserFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];

  public types: MailingTypeEnum[] = [];

  public channels: MailingChannelEnum[] = Object.values(MailingChannelEnum);
  public billingPaymentStatuses: BillPaymentStatusEnum[] = Object.values(BillPaymentStatusEnum);
  public attachmentStatuses: MailingAttachmentStatusEnum[] = Object.values(MailingAttachmentStatusEnum);

  public emailOptions: { label: string; value: EmailGateEnum }[] = [
    { label: 'Почта', value: EmailGateEnum.FRM },
    { label: 'MainSMS', value: EmailGateEnum.MAIN_SMS }
  ];

  public loadingFile = false;

  public columns: CrmTableColumn[] = [
    {
      label: 'Контрагент',
      name: 'contragent',
      sort: 'name:string',
      style: { 'min-width.px': 140 }
    },
    { label: 'Контакт', style: { 'min-width.px': 140 } },
    { label: 'Сумма', name: 'amount', sort: 'amount:number', style: { 'min-width.px': 100, 'max-width.px': 100 } },
    { style: { 'max-width.px': 35, 'min-width.px': 35 } }
  ];

  public today = moment().format('YYYY-MM-DD');

  public get MailingTypeEnum() {
    return MailingTypeEnum;
  }

  public get MailingChannelEnum() {
    return MailingChannelEnum;
  }

  public get EmailGateEnum() {
    return EmailGateEnum;
  }

  public constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly basisNotificationService: BasisNotificationService,
    private readonly financeAccessService: FinanceAccessService,
    private readonly errorService: ErrorService,
    private readonly accessService: AccessService
  ) {
    super();

    this.types = Object.values(MailingTypeEnum).filter((type) => {
      if (!this.accessService.debts?.debtsRights.canReadBillRegistry) {
        return type !== MailingTypeEnum.BILLS;
      }
      return true;
    });

    if (!this.data.planning) {
      this.data.planning = <PlanningUnitFormInterface>{};
    }

    const rights = this.financeAccessService.documentRights;
    this.types = this.types.filter((type) => {
      if (type === MailingTypeEnum.DOCUMENTS) {
        return rights.canReadDocument;
      }
      return true;
    });

    const isSelectedTypeAllowed = this.types.some((type) => type === this.data.type);
    if (!isSelectedTypeAllowed) {
      this.data.type = MailingTypeEnum.DEBTORS;
    }
  }

  public deleteContragentFromFile(debtorTarget: MailingFileDebtorTargetFormInterface) {
    this.data.fileDebtorTargets = this.data.fileDebtorTargets.filter((target) => target !== debtorTarget);
  }

  public toItemContactFromFile(contact: any): MailingFileDebtorTargetFormInterface {
    return contact as MailingFileDebtorTargetFormInterface;
  }

  public selectedFileDebtor(files: File[]) {
    if (files) {
      const formData = new FormData();
      formData.append('file', files[0], files[0].name);

      this.loadingFile = true;
      this.cdRef.markForCheck();

      this.basisNotificationService.debtorTargetsFromExcel(formData).subscribe({
        next: (result) => {
          this.data.fileDebtorTargets = result;
          this.loadingFile = false;
          this.cdRef.markForCheck();
        },
        error: (error: HttpErrorResponse) => {
          let str = 'Некорректно заполнены данные:';
          error.error?.validationErrors.forEach((item: MailingFileDebtorTargetFindInterface, index: number) => {
            if (item) {
              Object.keys(item).forEach((key: keyof MailingFileDebtorTargetFindInterface) => {
                if (item[key]) {
                  let char: string;
                  switch (key) {
                    case 'name': {
                      char = 'A';
                      break;
                    }
                    case 'phone': {
                      char = 'B';
                      break;
                    }
                    case 'email': {
                      char = 'C';
                      break;
                    }
                    case 'amount': {
                      char = 'D';
                      break;
                    }
                  }
                  str = str.concat(`\n- Ячейка ${char}${index + 2}: ${item[key]};`);
                }
              });
            }
          });
          this.errorService.addCustomError('Ошибка валидации', str);
          this.loadingFile = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public changeChannel(channel: MailingChannelEnum) {
    if (channel === MailingChannelEnum.EMAIL) {
      this.data.smsSender = null;
      this.data.emailSender = {
        gate: EmailGateEnum.FRM
      };
      if (this.emails?.length === 1) {
        this.data.emailSender.email = this.emails[0];
      }
    } else if (channel === MailingChannelEnum.SMS) {
      this.data.emailSender = null;
      if (this.arraySms.filter((sms) => sms.type === MainSmsIntegrationTypeEnum.SMS)?.length === 1) {
        this.data.smsSender = {
          gate: SmsGateEnum.MAIN_SMS,
          mainSmsAccount: this.arraySms.filter((sms) => sms.type === MainSmsIntegrationTypeEnum.SMS)[0]
        };
      }
    } else {
      this.data.emailSender = null;
      this.data.smsSender = null;
    }
  }

  public changeEmailType(type: EmailGateEnum) {
    if (type === EmailGateEnum.MAIN_SMS) {
      this.data.emailSender.email = null;
      if (this.arraySms.filter((sms) => sms.type === MainSmsIntegrationTypeEnum.EMAIL)?.length === 1) {
        this.data.emailSender.mainSmsAccount = this.arraySms.filter(
          (sms) => sms.type === MainSmsIntegrationTypeEnum.EMAIL
        )[0];
        if (this.data.emailSender.mainSmsAccount?.emails?.length === 1) {
          setTimeout(() => {
            this.data.emailSender.mainSmsAccountEmail = this.data.emailSender.mainSmsAccount.emails[0];
            this.cdRef.markForCheck();
          }, 100);
        }
      }
    } else {
      this.data.emailSender.mainSmsAccountEmail = '';
      this.data.emailSender.mainSmsAccount = null;
      if (this.emails?.length === 1) {
        this.data.emailSender.email = this.emails[0];
      }
    }
  }

  public changeEmailMainSms(email: MainSmsAccountFindInterface) {
    this.data.emailSender.mainSmsAccountEmail = '';
    if (email?.emails?.length === 1) {
      setTimeout(() => {
        this.data.emailSender.mainSmsAccountEmail = email.emails[0];
        this.cdRef.markForCheck();
      }, 100);
    }
  }

  public toData(data: any): MailingTemplateFormInterface {
    return data as MailingTemplateFormInterface;
  }
}
